/**
 * CAR (Corrective Action Report) Generation Page
 *
 * Page for consultants to generate Corrective Action Reports (CARs) based on
 * pre-audit findings, documenting issues and required actions.
 *
 * Features:
 * - CAR header information (auto-generated CAR number, client, date)
 * - Finding details section
 * - Severity selection (Minor/Major/Observation)
 * - Root cause analysis
 * - Corrective action required
 * - Responsible person assignment
 * - Target completion date
 * - Generate/save functionality
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, FileText, Lightbulb, Check, Circle } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { formatDate } from '../../utils/formatting';
import { mockApi } from '../../services/mockApi';
import type { Application } from '../../types/application';

type FindingSeverity = 'MAJOR' | 'MINOR' | 'OBSERVATION';

interface CARFormData {
  carNumber: string;
  findingTitle: string;
  findingDescription: string;
  severity: FindingSeverity;
  clauseReference: string;
  evidence: string;
  rootCauseAnalysis: string;
  correctiveActionRequired: string;
  responsiblePerson: string;
  targetCompletionDate: string;
  isDraft: boolean;
}

export const CARGenerationPage: React.FC = () => {
  const { t } = useTranslation(['common', 'consultant']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const applicationId = id || 'app-001';

  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState<Application | null>(null);
  const [saveMessage, setSaveMessage] = useState<string>('');

  // Initialize form with default values
  const [formData, setFormData] = useState<CARFormData>({
    carNumber: `CAR-${Date.now()}`,
    findingTitle: '',
    findingDescription: '',
    severity: 'MINOR',
    clauseReference: '',
    evidence: '',
    rootCauseAnalysis: '',
    correctiveActionRequired: '',
    responsiblePerson: '',
    targetCompletionDate: '',
    isDraft: true,
  });

  useEffect(() => {
    loadApplicationDetails();
  }, [applicationId]);

  const loadApplicationDetails = async () => {
    setLoading(true);
    try {
      const response = await mockApi.getApplication(applicationId);
      if (response.success && response.data) {
        setApplication(response.data);
      }
    } catch (error) {
      console.error('Error loading application:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CARFormData, value: string | FindingSeverity | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.findingTitle.trim()) {
      alert('Finding title is required');
      return false;
    }
    if (!formData.findingDescription.trim()) {
      alert('Finding description is required');
      return false;
    }
    if (!formData.rootCauseAnalysis.trim()) {
      alert('Root cause analysis is required');
      return false;
    }
    if (!formData.correctiveActionRequired.trim()) {
      alert('Corrective action is required');
      return false;
    }
    if (!formData.responsiblePerson.trim()) {
      alert('Responsible person is required');
      return false;
    }
    if (!formData.targetCompletionDate) {
      alert('Target completion date is required');
      return false;
    }
    return true;
  };

  const handleSaveDraft = () => {
    setFormData((prev) => ({ ...prev, isDraft: true }));
    setSaveMessage('CAR saved as draft');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleGenerateCAR = () => {
    if (!validateForm()) {
      return;
    }

    if (window.confirm('Generate Corrective Action Report? This will be sent to the client.')) {
      // In a real app, this would submit to the API
      alert(t('consultant:messages.carGenerated'));
      navigate(`/consultant/clients/${applicationId}`);
    }
  };

  const getSeverityColor = (severity: FindingSeverity): string => {
    switch (severity) {
      case 'MAJOR':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'MINOR':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'OBSERVATION':
        return 'bg-blue-100 text-blue-800 border-blue-300';
    }
  };

  if (loading) {
    return (
      <PageLayout
        pageTitle={t('consultant:car.title')}
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: t('consultant:dashboard.title'), href: '/consultant/dashboard' },
          { label: t('consultant:clients.title'), href: '/consultant/clients' },
          { label: t('consultant:car.title') },
        ]}
      >
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">{t('common:app.loading')}</p>
        </div>
      </PageLayout>
    );
  }

  if (!application) {
    return (
      <PageLayout
        pageTitle={t('consultant:car.title')}
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: t('consultant:dashboard.title'), href: '/consultant/dashboard' },
          { label: t('consultant:clients.title'), href: '/consultant/clients' },
          { label: t('consultant:car.title') },
        ]}
      >
        <div className="text-center py-12">
          <p className="text-gray-500">Application not found</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle={t('consultant:car.generate')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('consultant:dashboard.title'), href: '/consultant/dashboard' },
        { label: t('consultant:clients.title'), href: '/consultant/clients' },
        { label: application.companyName, href: `/consultant/clients/${applicationId}` },
        { label: t('consultant:car.generate') },
      ]}
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSaveDraft} icon={<Save className="w-4 h-4" />}>
            Save Draft
          </Button>
          <Button onClick={handleGenerateCAR} icon={<FileText className="w-4 h-4" />}>
            {t('consultant:car.generate')}
          </Button>
        </div>
      }
    >
      {saveMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {saveMessage}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - CAR Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* CAR Header */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">CAR Information</h3>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('consultant:car.reportNumber')}
                  </label>
                  <input
                    type="text"
                    value={formData.carNumber}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('consultant:car.issuedDate')}
                  </label>
                  <input
                    type="text"
                    value={formatDate(new Date().toISOString())}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company
                  </label>
                  <input
                    type="text"
                    value={application.companyName}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Finding Details */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">{t('consultant:car.findings')}</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Finding Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.findingTitle}
                    onChange={(e) => handleInputChange('findingTitle', e.target.value)}
                    placeholder="Brief title of the finding"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('consultant:car.findingType')} <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {(['MAJOR', 'MINOR', 'OBSERVATION'] as FindingSeverity[]).map((severity) => (
                      <button
                        key={severity}
                        type="button"
                        onClick={() => handleInputChange('severity', severity)}
                        className={`px-4 py-3 rounded-lg border-2 transition-all ${
                          formData.severity === severity
                            ? getSeverityColor(severity)
                            : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="text-sm font-medium">
                          {severity === 'MAJOR' && t('consultant:car.major')}
                          {severity === 'MINOR' && t('consultant:car.minor')}
                          {severity === 'OBSERVATION' && t('consultant:car.observation')}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('consultant:car.description')} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.findingDescription}
                    onChange={(e) => handleInputChange('findingDescription', e.target.value)}
                    placeholder="Detailed description of the non-conformity or observation"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('consultant:car.clauseReference')}
                  </label>
                  <input
                    type="text"
                    value={formData.clauseReference}
                    onChange={(e) => handleInputChange('clauseReference', e.target.value)}
                    placeholder="e.g., MHMS 2020 - 4.2.1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('consultant:car.evidence')}
                  </label>
                  <textarea
                    value={formData.evidence}
                    onChange={(e) => handleInputChange('evidence', e.target.value)}
                    placeholder="Evidence or observations supporting this finding"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Root Cause & Corrective Action */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">{t('consultant:car.correctiveAction')}</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('consultant:car.rootCause')} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.rootCauseAnalysis}
                    onChange={(e) => handleInputChange('rootCauseAnalysis', e.target.value)}
                    placeholder="Analyze the root cause of this non-conformity"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('consultant:car.proposedAction')} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.correctiveActionRequired}
                    onChange={(e) => handleInputChange('correctiveActionRequired', e.target.value)}
                    placeholder="Describe the corrective action required to address this finding"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('consultant:car.responsiblePerson')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.responsiblePerson}
                      onChange={(e) => handleInputChange('responsiblePerson', e.target.value)}
                      placeholder="Name or role"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('consultant:car.targetDate')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.targetCompletionDate}
                      onChange={(e) => handleInputChange('targetCompletionDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Sidebar - Summary & Actions */}
        <div className="space-y-6">
          {/* CAR Summary */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">CAR Summary</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500 mb-1">CAR Number</p>
                  <p className="font-mono text-sm font-medium">{formData.carNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Severity</p>
                  <Badge
                    variant={
                      formData.severity === 'MAJOR' ? 'error' :
                      formData.severity === 'MINOR' ? 'warning' : 'info'
                    }
                  >
                    {formData.severity}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Status</p>
                  <Badge variant={formData.isDraft ? 'default' : 'success'}>
                    {formData.isDraft ? 'Draft' : 'Issued'}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Company</p>
                  <p className="text-sm font-medium">{application.companyName}</p>
                </div>
                {formData.targetCompletionDate && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Due Date</p>
                    <p className="text-sm font-medium">{formatDate(formData.targetCompletionDate)}</p>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Completion Status */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Form Completion</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Finding Title</span>
                  {formData.findingTitle ? (
                    <Check className="w-4 h-4 text-success-600" />
                  ) : (
                    <Circle className="w-4 h-4 text-neutral-400" />
                  )}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Description</span>
                  {formData.findingDescription ? (
                    <Check className="w-4 h-4 text-success-600" />
                  ) : (
                    <Circle className="w-4 h-4 text-neutral-400" />
                  )}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Root Cause</span>
                  {formData.rootCauseAnalysis ? (
                    <Check className="w-4 h-4 text-success-600" />
                  ) : (
                    <Circle className="w-4 h-4 text-neutral-400" />
                  )}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Corrective Action</span>
                  {formData.correctiveActionRequired ? (
                    <Check className="w-4 h-4 text-success-600" />
                  ) : (
                    <Circle className="w-4 h-4 text-neutral-400" />
                  )}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Responsible Person</span>
                  {formData.responsiblePerson ? (
                    <Check className="w-4 h-4 text-success-600" />
                  ) : (
                    <Circle className="w-4 h-4 text-neutral-400" />
                  )}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Target Date</span>
                  {formData.targetCompletionDate ? (
                    <Check className="w-4 h-4 text-success-600" />
                  ) : (
                    <Circle className="w-4 h-4 text-neutral-400" />
                  )}
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Actions</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-2">
                <Button fullWidth onClick={handleSaveDraft} variant="outline" icon={<Save className="w-4 h-4" />}>
                  Save Draft
                </Button>
                <Button fullWidth onClick={handleGenerateCAR} icon={<FileText className="w-4 h-4" />}>
                  {t('consultant:car.generate')}
                </Button>
                <Button fullWidth variant="outline" onClick={() => navigate(`/consultant/clients/${applicationId}`)}>
                  ← Back to Client
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Help Text */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-primary-600" />
                <h3 className="text-lg font-semibold">Tips</h3>
              </div>
            </CardHeader>
            <CardBody>
              <div className="text-sm text-gray-600 space-y-2">
                <p><strong>Major:</strong> Critical non-conformity requiring immediate action</p>
                <p><strong>Minor:</strong> Non-conformity that should be addressed but not critical</p>
                <p><strong>Observation:</strong> Potential area for improvement</p>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

export default CARGenerationPage;

